import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTrack } from './TrackLayout';
import { trackApi, errMsg } from '../../lib/trackApi';
import TaskFilterBar, { filtersToParams } from '../../components/track/TaskFilterBar';
import TaskCreateForm from '../../components/track/TaskCreateForm';
import { StatusSelect } from '../../components/track/StatusBadge';
import {
  C, btn, btnGhost, btnSm, btnLink, input as inputStyle, card,
  todayYmd, addDays, fmtMD, fmtDateTime, dueInfo,
} from '../../components/track/trackUi';

const FILTER_KEY = 'track.dashFilters';

function loadFilters() {
  try {
    const v = JSON.parse(localStorage.getItem(FILTER_KEY) || '{}');
    return { boardId: null, assigneeId: null, status: '', q: '', starred: false, includeDone: false, starTop: true, ...v };
  } catch {
    return { boardId: null, assigneeId: null, status: '', q: '', starred: false, includeDone: false, starTop: true };
  }
}

function Stat({ label, value, color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...card, padding: '8px 14px', minWidth: 90, textAlign: 'left', cursor: 'pointer',
        borderColor: active ? C.brand : C.border, background: active ? C.brandSoft : '#fff',
      }}
    >
      <div style={{ fontSize: 12, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || '#1f2937' }}>{value}</div>
    </button>
  );
}

function DueHistoryModal({ taskId, onClose }) {
  const [rows, setRows] = useState(null);
  const [title, setTitle] = useState('');
  useEffect(() => {
    trackApi.getTask(taskId).then((d) => {
      setRows(d?.data?.dueHistory || []);
      setTitle(`${d?.data?.task?.taskNo || ''} ${d?.data?.task?.title || ''}`);
    }).catch(() => setRows([]));
  }, [taskId]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9100, background: 'rgba(0,0,0,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: 'min(460px, calc(100vw - 32px))', maxHeight: '70vh', overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>預計完成日歷史｜{title}</div>
        {!rows && <div style={{ color: C.muted }}>載入中…</div>}
        {rows && !rows.length && <div style={{ color: C.muted }}>尚無紀錄</div>}
        {(rows || []).map((h, i) => (
          <div key={h.id} style={{ padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
            <div style={{ color: C.muted, fontSize: 12 }}>
              {i + 1}. {fmtDateTime(h.createdAt)}｜{h.actorLabel || '系統'}{h.source === 'line' ? '・LINE' : ''}
            </div>
            <div>
              {h.oldValue ? `${fmtMD(h.oldValue)} → ` : ''}<strong>{h.newValue ? fmtMD(h.newValue) : '未設定'}</strong>
              {h.note ? `（${h.note}）` : ''}
            </div>
          </div>
        ))}
        <div style={{ textAlign: 'right', marginTop: 10 }}>
          <button type="button" style={btnGhost} onClick={onClose}>關閉</button>
        </div>
      </div>
    </div>
  );
}

function QuickPanel({ mode, task, onDone, onCancel }) {
  const [text, setText] = useState(mode === 'star' ? task.starNote || '' : '');
  const [date, setDate] = useState(mode === 'due' ? task.dueDate || '' : (mode === 'mute' ? task.muteUntil || addDays(todayYmd(), 1) : ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      if (mode === 'note') {
        if (!text.trim()) return;
        await trackApi.addNote(task.id, text.trim());
      } else if (mode === 'due') {
        await trackApi.patchTask(task.id, { dueDate: date || null, reason: text.trim() || undefined });
      } else if (mode === 'mute') {
        await trackApi.patchTask(task.id, { muteUntil: date || null });
      } else if (mode === 'star') {
        await trackApi.patchTask(task.id, { starNote: text.trim() });
      }
      onDone();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '8px 0 4px 34px' }} onClick={(e) => e.stopPropagation()}>
      {(mode === 'due' || mode === 'mute') && (
        <input type="date" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} />
      )}
      {mode !== 'mute' && (
        <input
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
          autoFocus
          placeholder={mode === 'note' ? '備註內容（自動記錄時間）' : mode === 'due' ? '調整原因（選填）' : '星號備註'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
        />
      )}
      {mode === 'mute' && <span style={{ fontSize: 12, color: C.muted }}>此日（含）以前不發每日通知</span>}
      <button type="button" style={{ ...btn, ...btnSm }} disabled={busy} onClick={submit}>確定</button>
      {mode === 'mute' && task.muteUntil && (
        <button type="button" style={{ ...btnGhost, ...btnSm }} disabled={busy} onClick={() => { setDate(''); trackApi.patchTask(task.id, { muteUntil: null }).then(onDone).catch((e) => setError(errMsg(e))); }}>
          恢復通知
        </button>
      )}
      <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={onCancel}>取消</button>
      {error && <span style={{ color: C.danger, fontSize: 12 }}>{error}</span>}
    </div>
  );
}

export default function TrackDashboard() {
  const { boards, members, boardById, openTask, refreshKey, bumpRefresh } = useTrack();
  const [date, setDate] = useState(todayYmd());
  const [filters, setFilters] = useState(loadFilters);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, overdue: 0, dueToday: 0, starred: 0 });
  const [quickFilter, setQuickFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quick, setQuick] = useState({ id: null, mode: null });
  const [historyId, setHistoryId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const qTimer = useRef(null);
  const today = todayYmd();

  useEffect(() => { localStorage.setItem(FILTER_KEY, JSON.stringify(filters)); }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await trackApi.dashboard({ date, ...filtersToParams(filters) });
      if (!d?.ok) throw new Error(d?.error || '載入失敗');
      setTasks(d.data.tasks || []);
      setStats(d.data.stats || {});
      setError('');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [date, filters]);

  useEffect(() => {
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(load, 250);
    return () => clearTimeout(qTimer.current);
  }, [load, refreshKey]);

  const shown = useMemo(() => {
    let list = tasks;
    if (quickFilter === 'overdue') list = list.filter((t) => t.dueDate && t.dueDate < date && !t.doneAt);
    if (quickFilter === 'dueToday') list = list.filter((t) => t.dueDate === date && !t.doneAt);
    if (quickFilter === 'starred') list = list.filter((t) => t.isStarred);
    if (filters.starTop) list = [...list].sort((a, b) => (b.isStarred - a.isStarred) || (a.sortOrder - b.sortOrder) || (a.id - b.id));
    return list;
  }, [tasks, quickFilter, filters.starTop, date]);

  const afterChange = () => {
    setQuick({ id: null, mode: null });
    load();
    bumpRefresh();
  };

  const patch = async (task, body) => {
    try {
      await trackApi.patchTask(task.id, body);
      afterChange();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const onDrop = async (targetId) => {
    const from = shown.findIndex((t) => t.id === dragId);
    const to = shown.findIndex((t) => t.id === targetId);
    setDragId(null);
    setOverId(null);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...shown];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setTasks((prev) => {
      const ids = new Set(next.map((t) => t.id));
      return [...next, ...prev.filter((t) => !ids.has(t.id))];
    });
    try {
      await trackApi.reorderTasks(next.map((t) => t.id));
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const activeMembers = members.filter((m) => m.isActive);
  const chip = (on) => ({
    padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    border: `1px solid ${on ? C.brandDark : '#d1d5db'}`, background: on ? C.brand : '#fff', color: on ? '#fff' : '#1f2937',
  });

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => setDate(addDays(date, -1))}>‹</button>
        <input type="date" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value || todayYmd())} />
        <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => setDate(addDays(date, 1))}>›</button>
        {date !== today && <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => setDate(today)}>今天</button>}
        <div style={{ marginLeft: 'auto' }}>
          <button type="button" style={btn} onClick={() => setShowCreate((v) => !v)}>{showCreate ? '收合' : '＋ 新增任務'}</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <button type="button" style={chip(!filters.assigneeId)} onClick={() => setFilters((f) => ({ ...f, assigneeId: null }))}>全部</button>
        {activeMembers.map((m) => (
          <button key={m.id} type="button" style={chip(filters.assigneeId === m.id)} onClick={() => setFilters((f) => ({ ...f, assigneeId: m.id }))}>
            {m.name}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 10 }}>
        <TaskFilterBar boards={boards} members={members} value={filters} onChange={(v) => setFilters((f) => ({ ...f, ...v }))} show={{ assignee: false }}>
          <label style={{ fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={!!filters.starTop} onChange={(e) => setFilters((f) => ({ ...f, starTop: e.target.checked }))} />
            星號置頂
          </label>
        </TaskFilterBar>
      </div>

      {showCreate && (
        <div style={{ marginBottom: 12, maxWidth: 720 }}>
          <TaskCreateForm
            boards={boards}
            members={members}
            boardId={filters.boardId || undefined}
            defaultAssigneeId={filters.assigneeId}
            onCreated={() => { setShowCreate(false); afterChange(); }}
            onCancel={() => setShowCreate(false)}
          />
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <Stat label="應執行" value={stats.total || 0} active={!quickFilter} onClick={() => setQuickFilter('')} />
        <Stat label="逾期" value={stats.overdue || 0} color={C.danger} active={quickFilter === 'overdue'} onClick={() => setQuickFilter(quickFilter === 'overdue' ? '' : 'overdue')} />
        <Stat label="今日到期" value={stats.dueToday || 0} color="#d97706" active={quickFilter === 'dueToday'} onClick={() => setQuickFilter(quickFilter === 'dueToday' ? '' : 'dueToday')} />
        <Stat label="星號" value={stats.starred || 0} color={C.star} active={quickFilter === 'starred'} onClick={() => setQuickFilter(quickFilter === 'starred' ? '' : 'starred')} />
      </div>

      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}
      {!boards.length && <div style={{ color: C.muted, padding: '16px 0' }}>尚無看板，請先到「看板」建立。</div>}
      {loading && !tasks.length && <div style={{ color: C.muted }}>載入中…</div>}
      {!loading && boards.length > 0 && !shown.length && <div style={{ color: C.muted, padding: '16px 0' }}>沒有符合的任務</div>}

      <div>
        {shown.map((t) => {
          const board = boardById.get(t.boardId);
          const due = dueInfo(t, date, board);
          const isQuick = quick.id === t.id;
          return (
            <div
              key={t.id}
              draggable
              onDragStart={(e) => { setDragId(t.id); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={(e) => { e.preventDefault(); if (overId !== t.id) setOverId(t.id); }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              onDrop={(e) => { e.preventDefault(); onDrop(t.id); }}
              style={{
                ...card,
                padding: '8px 10px',
                marginBottom: 6,
                opacity: dragId === t.id ? 0.4 : 1,
                borderColor: overId === t.id && dragId !== t.id ? C.brand : (t.isStarred ? '#fcd34d' : C.border),
                background: t.isStarred ? '#fffdf5' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span title="拖曳排序" style={{ cursor: 'grab', color: '#9ca3af', userSelect: 'none' }}>⋮⋮</span>
                <button
                  type="button"
                  title={t.isStarred ? '取消星號' : '加星號'}
                  onClick={() => patch(t, { isStarred: !t.isStarred })}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: t.isStarred ? C.star : '#d1d5db', padding: 0 }}
                >
                  {t.isStarred ? '★' : '☆'}
                </button>
                <span style={{ fontSize: 12, color: C.muted, minWidth: 52 }}>{t.taskNo}</span>
                <button
                  type="button"
                  onClick={() => openTask(t.id)}
                  style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 700, fontSize: 14, textAlign: 'left', flex: '1 1 200px', color: '#111' }}
                >
                  {t.title}
                  {t.childCount > 0 && <span style={{ fontSize: 12, color: C.muted, fontWeight: 400 }}>（子任務 {t.childDone}/{t.childCount}）</span>}
                  {t.attachCount > 0 && <span style={{ fontSize: 12, color: C.muted, fontWeight: 400 }}>（附件 {t.attachCount}）</span>}
                </button>
                <span style={{ fontSize: 12, background: '#f3f4f6', borderRadius: 4, padding: '1px 6px' }}>{t.boardName}</span>
                {t.parentNo && <button type="button" style={btnLink} onClick={() => openTask(t.parentId)}>屬 {t.parentNo}</button>}
                <span style={{ fontSize: 13, minWidth: 56 }}>{t.assigneeName || <span style={{ color: '#9ca3af' }}>未指派</span>}</span>
                <StatusSelect statuses={board?.statuses} value={t.status} onChange={(v) => patch(t, { status: v })} />
                <span style={{ fontSize: 13, color: due.color, minWidth: 96 }}>
                  {due.text}
                  <button type="button" style={{ ...btnLink, marginLeft: 4 }} onClick={() => setHistoryId(t.id)}>歷史</button>
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 4, paddingLeft: 34, fontSize: 12 }}>
                {t.isStarred && t.starNote && <span style={{ color: '#b45309' }}>★ {t.starNote}</span>}
                {t.lastNote && (
                  <span style={{ color: '#374151', flex: '1 1 240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.lastNote}>
                    <span style={{ color: C.muted }}>{fmtDateTime(t.lastNoteAt)}</span> {t.lastNote}
                  </span>
                )}
                {t.muteUntil && t.muteUntil >= today && <span style={{ color: C.muted }}>通知暫停至 {fmtMD(t.muteUntil)}</span>}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                  <button type="button" style={btnLink} onClick={() => setQuick({ id: t.id, mode: 'note' })}>＋備註</button>
                  <button type="button" style={btnLink} onClick={() => setQuick({ id: t.id, mode: 'due' })}>改日期</button>
                  <button type="button" style={btnLink} onClick={() => setQuick({ id: t.id, mode: 'mute' })}>暫停通知</button>
                  {t.isStarred && <button type="button" style={btnLink} onClick={() => setQuick({ id: t.id, mode: 'star' })}>星號備註</button>}
                </span>
              </div>
              {isQuick && (
                <QuickPanel
                  key={`${t.id}-${quick.mode}`}
                  mode={quick.mode}
                  task={t}
                  onDone={afterChange}
                  onCancel={() => setQuick({ id: null, mode: null })}
                />
              )}
            </div>
          );
        })}
      </div>

      {historyId && <DueHistoryModal taskId={historyId} onClose={() => setHistoryId(null)} />}
    </div>
  );
}
